import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useCustomGroups } from '../../hooks/useCustomGroups';
import { createCustomGroup as apiCreateCustomGroup, deleteCustomGroup as apiDeleteCustomGroup, validateCustomGroupExists } from '../../api/customGroupsHttpClient';
import ActionConfirmation from '../../../../Common/components/ActionConfirmation/ActionConfirmation';
import CreateCustomGroupDialog from '../CreateCustomGroupDialog/CreateCustomGroupDialog';
import CustomGroupsTable from '../CustomGroupsTable/CustomGroupsTable';
import EditCustomGroupUsersDialog from '../EditCustomGroupUsersDialog/EditCustomGroupUsersDialog';
import { CustomGroup } from '../../api/models/CustomGroup';

interface CustomGroupsProps {
  projectId: string;
  toolId: string;
  toolProjectId: string;
}

const CustomGroups: React.FC<CustomGroupsProps> = ({
  projectId,
  toolId,
  toolProjectId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    customGroups,
    loading,
    error,
    fetchCustomGroups,
  } = useCustomGroups();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [groupNameExists, setGroupNameExists] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    groupName: string;
  }>({ show: false, groupName: '' });
  const [editUsersDialog, setEditUsersDialog] = useState<{
    open: boolean;
    group: CustomGroup | null;
  }>({ open: false, group: null });

  useEffect(() => {
    fetchCustomGroups(projectId, toolId);
  }, [fetchCustomGroups, projectId, toolId]);

  // Debounced validation
  const debouncedValidation = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (name: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          checkGroupNameExists(name);
        }, 500);
      };
    })(),
    [projectId, toolId, toolProjectId]
  );

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setCreateError('Group name is required');
      return;
    }

    const groupNameToCreate = newGroupName.trim();
    
    // Final validation before creating
    try {
      const exists = await validateCustomGroupExists(
        projectId,
        toolId,
        toolProjectId,
        groupNameToCreate
      );
      
      if (exists) {
        setCreateError('A group with this name already exists');
        return;
      }
    } catch (err) {
      setCreateError('Error validating group name');
      return;
    }

    setCreateError(null);
    setIsCreateDialogOpen(false);
    setNewGroupName('');
    setIsLoading(true);
    
    try {
      await apiCreateCustomGroup({
        projectId,
        toolId,
        toolProjectId,
        groupName: groupNameToCreate,
      });
      
      // Show success toast
      enqueueSnackbar(`Custom group "${groupNameToCreate}" has been created successfully!`, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      
      // Refresh list
      await fetchCustomGroups(projectId, toolId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setCreateError(errorMessage);
      
      // Show error toast
      enqueueSnackbar(`Failed to create custom group: ${errorMessage}`, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGroup = (groupName: string) => {
    setDeleteConfirmation({ show: true, groupName });
  };

  const handleEditGroup = (groupName: string) => {
    const groupToEdit = customGroups.find(g => g.groupName === groupName);
    if (groupToEdit) {
      setEditUsersDialog({ open: true, group: groupToEdit });
    }
  };

  const handleCloseEditDialog = () => {
    setEditUsersDialog({ open: false, group: null });
  };

  const confirmDeleteGroup = async () => {
    const deletedGroupName = deleteConfirmation.groupName;
    
    // Close confirmation dialog immediately
    setDeleteConfirmation({ show: false, groupName: '' });
    
    // Start loading (Backdrop will show)
    setIsLoading(true);
    
    try {
      await apiDeleteCustomGroup({
        projectId,
        toolId,
        toolProjectId,
        groupName: deletedGroupName,
      });
      
      // Show success toast
      enqueueSnackbar(`Custom group "${deletedGroupName}" has been deleted successfully!`, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      
      // Refresh list
      await fetchCustomGroups(projectId, toolId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      
      // Show error toast
      enqueueSnackbar(`Failed to delete custom group: ${errorMessage}`, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      
      console.error('Failed to delete group:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDeleteGroup = () => {
    setDeleteConfirmation({ show: false, groupName: '' });
  };

  const checkGroupNameExists = async (name: string) => {
    if (!name.trim()) {
      setGroupNameExists(false);
      return;
    }

    setIsValidating(true);
    try {
      const exists = await validateCustomGroupExists(
        projectId,
        toolId,
        toolProjectId,
        name.trim()
      );
      setGroupNameExists(exists);
    } catch (err) {
      console.error('Error validating group name:', err);
      setGroupNameExists(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleGroupNameChange = (name: string) => {
    setNewGroupName(name);
    // Clear error when user starts typing
    if (createError) {
      setCreateError(null);
    }
    // Reset validation state and start debounced validation
    setGroupNameExists(false);
    debouncedValidation(name);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewGroupName('');
    setCreateError(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 1
      }}>
        <Typography variant="h6">Custom Groups</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={isLoading}
        >
          Create Group
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <CustomGroupsTable
          customGroups={customGroups}
          isLoading={isLoading}
          onDeleteGroup={handleDeleteGroup}
          onEditGroup={handleEditGroup}
        />
      )}
      </Box>

      <CreateCustomGroupDialog
        open={isCreateDialogOpen}
        newGroupName={newGroupName}
        createError={createError}
        isLoading={isLoading}
        isValidating={isValidating}
        groupNameExists={groupNameExists}
        onClose={handleCloseCreateDialog}
        onGroupNameChange={handleGroupNameChange}
        onCreateGroup={handleCreateGroup}
      />

 
      <EditCustomGroupUsersDialog
        open={editUsersDialog.open}
        onClose={handleCloseEditDialog}
        group={editUsersDialog.group}
        projectId={projectId}
        toolId={toolId}
        toolProjectId={toolProjectId}
      />

      <ActionConfirmation
        when={deleteConfirmation.show}
        title="Delete Custom Group"
        description={`Are you sure you want to delete the group "${deleteConfirmation.groupName}"? This action cannot be undone.`}
        confirm={confirmDeleteGroup}
        cancel={cancelDeleteGroup}
      />
      
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
       </Backdrop>
    </Box>
  );
};

export default CustomGroups;
